/**
 * Shared factory for monograph-backed demo panel primitives.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import {
  createMonographPanelTokens,
  standardMonographPanelTokens,
} from "./MonographPanelTokens.js";
import type { SHARED_MONOGRAPH_PANEL_TOKENS } from "./MonographPanelTokens.js";
import { createMonographPanelPrimitives } from "./MonographPanelPrimitives.js";
import {
  MonographChoiceChip,
  MonographChoiceGroup,
  MonographControlBar,
  MonographControlButton,
  MonographNumberStepper,
  type MonographChoiceChipProps,
  type MonographChoiceGroupProps,
  type MonographControlButtonProps,
  type MonographNumberStepperProps,
} from "./MonographPanelControls.js";
import {
  MonographSelectableCard,
  type MonographSelectableCardProps,
} from "./MonographSelectableCard.js";
import { MonographFormulaCard, type MonographFormulaCardProps } from "./MonographFormulaCard.js";
import { MonographAccentCard, type MonographAccentCardProps } from "./MonographAccentCard.js";
import {
  MonographDisclosureRow,
  type MonographDisclosureRowProps,
} from "./MonographDisclosureRow.js";
import { MonographSurfaceBox, type MonographSurfaceBoxProps } from "./MonographSurfaceBox.js";
import { MonographSurfaceLink, type MonographSurfaceLinkProps } from "./MonographSurfaceLink.js";
import { MonographTextField, type MonographTextFieldProps } from "./MonographTextField.js";
import {
  MonographStepperBar,
  MonographToggleChip,
  type MonographStepperBarProps,
  type MonographToggleChipProps,
} from "./MonographPanelNavigationControls.js";
import {
  createMonographPanelSectionSurfaces,
  type MonographBadgeGroupProps,
  type MonographBulletedListProps,
  type MonographConceptCardProps,
  type MonographLabelValueRowProps,
  type MonographSectionLabelProps,
  type MonographStackProps,
  type MonographTaggedPanelShellProps,
  type MonographTintedRowProps,
} from "./MonographPanelSectionSurfaces.js";
import type {
  MonographPanelPrimitiveOptions,
  MonographPanelPrimitives,
  MonographPanelTokens,
} from "./MonographPanelPrimitiveTypes.js";

type WithoutTokens<TProps> = Omit<TProps, "tokens">;

export interface BoundMonographPanelControls<ResolvedTokens extends MonographPanelTokens> {
  readonly tokens: ResolvedTokens;
  readonly ControlBar: typeof MonographControlBar;
  readonly ControlButton: (props: WithoutTokens<MonographControlButtonProps>) => React.JSX.Element;
  readonly ChoiceChip: <Id extends string = string>(
    props: WithoutTokens<MonographChoiceChipProps<Id>>,
  ) => React.JSX.Element;
  readonly ChoiceGroup: <Id extends string = string>(
    props: WithoutTokens<MonographChoiceGroupProps<Id>>,
  ) => React.JSX.Element;
  readonly NumberStepper: (props: WithoutTokens<MonographNumberStepperProps>) => React.JSX.Element;
  readonly SelectableCard: <Id extends string = string>(
    props: WithoutTokens<MonographSelectableCardProps<Id>>,
  ) => React.JSX.Element;
  readonly FormulaCard: (props: WithoutTokens<MonographFormulaCardProps>) => React.JSX.Element;
  readonly AccentCard: (props: WithoutTokens<MonographAccentCardProps>) => React.JSX.Element;
  readonly SurfaceBox: (props: WithoutTokens<MonographSurfaceBoxProps>) => React.JSX.Element;
  readonly DisclosureRow: (props: WithoutTokens<MonographDisclosureRowProps>) => React.JSX.Element;
  readonly SurfaceLink: (props: WithoutTokens<MonographSurfaceLinkProps>) => React.JSX.Element;
  readonly TextField: (props: WithoutTokens<MonographTextFieldProps>) => React.JSX.Element;
  readonly StepperBar: (props: WithoutTokens<MonographStepperBarProps>) => React.JSX.Element;
  readonly ToggleChip: (props: WithoutTokens<MonographToggleChipProps>) => React.JSX.Element;
  readonly BadgeGroup: (props: MonographBadgeGroupProps) => React.JSX.Element;
  readonly BulletedList: (props: MonographBulletedListProps) => React.JSX.Element;
  readonly ConceptCard: (props: MonographConceptCardProps) => React.JSX.Element;
  readonly LabelValueRow: (props: MonographLabelValueRowProps) => React.JSX.Element;
  readonly SectionLabel: (props: MonographSectionLabelProps) => React.JSX.Element;
  readonly Stack: (props: MonographStackProps) => React.JSX.Element;
  readonly TaggedPanelShell: (props: MonographTaggedPanelShellProps) => React.JSX.Element;
  readonly TintedRow: (props: MonographTintedRowProps) => React.JSX.Element;
}

export type MonographPanelSharedTokens<TokenData extends Record<string, string>> =
  MonographPanelTokens & typeof SHARED_MONOGRAPH_PANEL_TOKENS & TokenData;

export type MonographPanelSharedOptions<TokenData extends Record<string, string>> =
  | MonographPanelPrimitiveOptions
  | ((tokens: MonographPanelSharedTokens<TokenData>) => MonographPanelPrimitiveOptions);

export interface MonographPanelShared<TokenData extends Record<string, string>>
  extends
    MonographPanelPrimitives,
    BoundMonographPanelControls<MonographPanelSharedTokens<TokenData>> {}

export type BoundMonographPanelSharedOptions<ResolvedTokens extends MonographPanelTokens> =
  | MonographPanelPrimitiveOptions
  | ((tokens: ResolvedTokens) => MonographPanelPrimitiveOptions);

export interface BoundMonographPanelShared<ResolvedTokens extends MonographPanelTokens>
  extends MonographPanelPrimitives, BoundMonographPanelControls<ResolvedTokens> {}

export type StandardMonographPanelKitOptions<ResolvedTokens extends MonographPanelTokens> =
  BoundMonographPanelSharedOptions<ResolvedTokens>;

export function bindMonographPanelShared<ResolvedTokens extends MonographPanelTokens>(
  tokens: ResolvedTokens,
  options: BoundMonographPanelSharedOptions<ResolvedTokens> = {},
): BoundMonographPanelShared<ResolvedTokens> {
  const resolvedOptions = typeof options === "function" ? options(tokens) : options;
  const primitives = createMonographPanelPrimitives(tokens, resolvedOptions);
  const sectionSurfaces = createMonographPanelSectionSurfaces({
    BadgeRail: primitives.BadgeRail,
    Card: primitives.Card,
    PanelShell: primitives.PanelShell,
    tokens,
  });
  return {
    tokens,
    ControlBar: MonographControlBar,
    ControlButton: (props) => MonographControlButton({ tokens, ...props }),
    ChoiceChip: <Id extends string = string>(props: WithoutTokens<MonographChoiceChipProps<Id>>) =>
      MonographChoiceChip<Id>({ tokens, ...props }),
    ChoiceGroup: <Id extends string = string>(
      props: WithoutTokens<MonographChoiceGroupProps<Id>>,
    ) => MonographChoiceGroup<Id>({ tokens, ...props }),
    NumberStepper: (props) => MonographNumberStepper({ tokens, ...props }),
    SelectableCard: <Id extends string = string>(
      props: WithoutTokens<MonographSelectableCardProps<Id>>,
    ) => MonographSelectableCard<Id>({ tokens, ...props }),
    FormulaCard: (props) => MonographFormulaCard({ tokens, ...props }),
    AccentCard: (props) => MonographAccentCard({ tokens, ...props }),
    SurfaceBox: (props) => MonographSurfaceBox({ tokens, ...props }),
    DisclosureRow: (props) => MonographDisclosureRow({ tokens, ...props }),
    SurfaceLink: (props) => MonographSurfaceLink({ tokens, ...props }),
    TextField: (props) => MonographTextField({ tokens, ...props }),
    StepperBar: (props) => MonographStepperBar({ tokens, ...props }),
    ToggleChip: (props) => MonographToggleChip({ tokens, ...props }),
    ...sectionSurfaces,
    ...primitives,
  };
}

export function createMonographPanelShared<TokenData extends Record<string, string>>(
  tokensData: TokenData,
  options: MonographPanelSharedOptions<TokenData> = {},
): MonographPanelShared<TokenData> {
  const tokens = createMonographPanelTokens(tokensData);
  return bindMonographPanelShared(tokens, options);
}

function standardMonographPanelOptions<ResolvedTokens extends MonographPanelTokens>(
  tokens: ResolvedTokens,
  options: StandardMonographPanelKitOptions<ResolvedTokens> = {},
): MonographPanelPrimitiveOptions {
  const override = typeof options === "function" ? options(tokens) : options;
  return {
    panel: {
      contentGap: 18,
      dividerMaxWidth: 260,
      dividerOpacity: 0.82,
      dividerShadow: true,
      gradientTitle: true,
      maxWidth: 1120,
      padding: "32px 32px 72px",
      subtitleMargin: "8px 0 0",
      titleFontSize: 28,
      titleLineHeight: 1.12,
      ...override.panel,
    },
    card: {
      background: "rgba(10, 15, 28, 0.72)",
      borderRadius: 8,
      className:
        "outline outline-1 outline-white/5 transition-[outline-color,transform] duration-200 hover:outline-sky-300/20",
      overflow: "hidden",
      trackPointerSpot: true,
      ...override.card,
    },
    chip: {
      inactiveBackground: "rgba(7, 10, 19, 0.45)",
      ...override.chip,
    },
    kpi: {
      background: "rgba(7, 10, 19, 0.58)",
      labelMarginTop: 5,
      minWidth: 96,
      padding: "14px 16px",
      textShadowOpacity: "50",
      valueFontSize: 24,
      ...override.kpi,
    },
    mono: {
      background: "rgba(7, 10, 19, 0.74)",
      lineHeight: 1.6,
      ...override.mono,
    },
    sectionHeading: {
      margin: "4px 0 6px",
      ...override.sectionHeading,
    },
    verdict: {
      neutralColor: tokens.accentBlue ?? tokens.accent,
      ...override.verdict,
    },
    ...(override.badge ? { badge: override.badge } : {}),
  };
}

export function bindStandardMonographPanelKit<ResolvedTokens extends MonographPanelTokens>(
  tokens: ResolvedTokens,
  options: StandardMonographPanelKitOptions<ResolvedTokens> = {},
): BoundMonographPanelShared<ResolvedTokens> {
  return bindMonographPanelShared(tokens, standardMonographPanelOptions(tokens, options));
}

export function createStandardMonographPanelKit<TokenData extends Record<string, string>>(
  tokensData: TokenData,
  options: MonographPanelSharedOptions<TokenData> = {},
): MonographPanelShared<TokenData> {
  const tokens = createMonographPanelTokens(tokensData);
  return bindStandardMonographPanelKit(tokens, options);
}

export const PanelSurface = bindStandardMonographPanelKit(standardMonographPanelTokens);
export const panelTokens = standardMonographPanelTokens;
